import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { getPartnerSession, hasActivePartnerSession } from '../../lib/utils/partner-session'
import { getPartnerErrorMessage, PARTNER_INVESTOR_PAGE_SIZE, partnerServices } from '../../services'
import { PartnerInvestor } from '../../services/partner.types'
import { PartnerInvestorDetail } from './partner-investor-detail'
import { PartnerShell, PartnerStatus } from './partner-shell'

const PartnerInvestorsContainer = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const page = Math.max(1, Number(searchParams.get('page')) || 1)
  const [investors, setInvestors] = useState<PartnerInvestor[]>([])
  const [total, setTotal] = useState(0)
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [allowed, setAllowed] = useState(false)

  useEffect(() => {
    if (!hasActivePartnerSession()) {
      navigate('/partner', { replace: true })
      return
    }
    const session = getPartnerSession()
    if (!session?.passwordResetUsed) {
      navigate('/partner/set-password', { replace: true })
      return
    }
    setAllowed(true)

    let cancelled = false
    const loadInvestors = async () => {
      setIsLoading(true)
      setError('')
      try {
        const data = await partnerServices.getInvestors(page, PARTNER_INVESTOR_PAGE_SIZE)
        if (!cancelled) {
          setInvestors(data.investors)
          setTotal(data.total)
          setSelectedId((current) =>
            data.investors.some((investor) => investor.investorId === current) ? current : null,
          )
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(getPartnerErrorMessage(loadError, 'Unable to load investors.'))
          setInvestors([])
          setTotal(0)
        }
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    loadInvestors()
    return () => {
      cancelled = true
    }
  }, [navigate, page])

  if (!allowed) return null

  const session = getPartnerSession()
  const totalPages = Math.max(1, Math.ceil(total / PARTNER_INVESTOR_PAGE_SIZE))
  const rangeStart = total === 0 ? 0 : (page - 1) * PARTNER_INVESTOR_PAGE_SIZE + 1
  const rangeEnd = Math.min(page * PARTNER_INVESTOR_PAGE_SIZE, total)
  const selected = investors.find((investor) => investor.investorId === selectedId) || null

  const setPage = (nextPage: number) => {
    setSearchParams({ page: String(nextPage) })
  }

  return (
    <PartnerShell>
      {selected ? (
        <PartnerInvestorDetail investor={selected} onBack={() => setSelectedId(null)} />
      ) : (
        <>
          <div className='partner-page-heading'>
            <div>
              <h1>Investor book</h1>
              <p>
                {session?.referralCode
                  ? `Investors who signed up with referral code ${session.referralCode}.`
                  : 'Investors who signed up with your referral code.'}
              </p>
            </div>
          </div>

          {error ? (
            <p className='partner-login__error' role='alert'>
              {error}
            </p>
          ) : null}

          <section className='partner-card' aria-label='Investor book'>
            {isLoading ? <p className='partner-empty'>Loading investors...</p> : null}
            {!isLoading && !error && investors.length === 0 ? (
              <p className='partner-empty'>No investors yet.</p>
            ) : null}
            {!isLoading && investors.length > 0 ? (
              <div className='partner-table-scroll'>
                <table className='partner-table'>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Client ID</th>
                      <th>Account</th>
                      <th>KYC</th>
                    </tr>
                  </thead>
                  <tbody>
                    {investors.map((investor) => {
                      const openInvestor = () => setSelectedId(investor.investorId)
                      return (
                        <tr
                          key={investor.investorId}
                          className='partner-table__row'
                          role='button'
                          tabIndex={0}
                          aria-label={investor.name || 'Unnamed investor'}
                          onClick={openInvestor}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault()
                              openInvestor()
                            }
                          }}
                        >
                          <td className='partner-table__name'>
                            {investor.name || 'Unnamed investor'}
                          </td>
                          <td>{investor.investorReference || '—'}</td>
                          <td>
                            <PartnerStatus status={investor.accountStatus} />
                          </td>
                          <td>
                            <PartnerStatus status={investor.kyc?.status} />
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}

            {!isLoading && !error && total > 0 ? (
              <div className='partner-pager'>
                <p>
                  {rangeStart}–{rangeEnd} of {total}
                </p>
                <div className='partner-pager__controls'>
                  <button type='button' onClick={() => setPage(page - 1)} disabled={page <= 1}>
                    Previous
                  </button>
                  <span>
                    Page {page} of {totalPages}
                  </span>
                  <button
                    type='button'
                    onClick={() => setPage(page + 1)}
                    disabled={page >= totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : null}
          </section>
        </>
      )}
    </PartnerShell>
  )
}

export { PartnerInvestorsContainer }
